import React, { useEffect, useState } from 'react';
import './InfoOverlay.css';

/**
 * Small ⓘ button that toggles a centered modal explaining (1) how to read the
 * transfer-entropy graph and (2) what the Volatility Watch score is. Button-only
 * (no auto-open). Closes on ✕, backdrop click, or Esc.
 */
export const InfoOverlay = ({ assetType }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('graph');
  const isForex = assetType === 'Forex';

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const themed = isForex ? 'forex-theme' : '';

  return (
    <>
      <button
        className={`info-btn ${themed}`}
        aria-label="About these graphs and the volatility score"
        title="What is this?"
        onClick={() => setOpen(true)}
      >i</button>

      {open && (
        <div className="info-backdrop" onClick={() => setOpen(false)}>
          <div className={`info-modal ${themed}`} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="info-modal-head">
              <span className="info-modal-title">Information Guide</span>
              <button className="info-close" aria-label="Close" onClick={() => setOpen(false)}>&times;</button>
            </div>

            <div className="info-tabs">
              <button className={`info-tab ${tab === 'graph' ? 'active' : ''}`} onClick={() => setTab('graph')}>Reading the graph</button>
              <button className={`info-tab ${tab === 'expanded' ? 'active' : ''}`} onClick={() => setTab('expanded')}>Expanded graph</button>
              <button className={`info-tab ${tab === 'score' ? 'active' : ''}`} onClick={() => setTab('score')}>Volatility Watch</button>
            </div>

            <div className="info-body">
              {tab === 'graph' && (
                <>
                  <p>Each view is a <b>transfer-entropy network</b> &mdash; a daily snapshot of how price moves flow between assets.</p>
                  <ul>
                    <li><b>Nodes</b> are assets (stocks, crypto, or forex pairs).</li>
                    <li><b>Arrows</b> are directional <i>information flow</i>: an arrow A&rarr;B means A&apos;s recent moves carry information about B&apos;s <i>near-future</i> moves. This is causation-flavoured, not just correlation &mdash; similar assets that simply move together often have <i>low</i> transfer entropy.</li>
                    <li>We keep only <b>statistically significant</b> links (effective transfer entropy, p&nbsp;&lt;&nbsp;0.005).</li>
                  </ul>
                  <p>Click a node to see its metrics: <b>in/out degree</b> (how connected), <b>betweenness</b> (bridge role), <b>clustering</b> (tight-knit neighbourhood), its <b>most-significant connections</b>, and a <b>weekly degree</b> chart over time.</p>
                  <p className="info-tip">Tip: peripheral, lightly-connected nodes aren&apos;t unimportant &mdash; they&apos;re often the most volatile. See the Volatility Watch tab.</p>
                </>
              )}
              {tab === 'expanded' && (
                <>
                  <p>The graph you <i>see</i> is a curated subset &mdash; only the <b>strongest ~500 connections</b> are drawn, so it stays readable. The full network would be an unreadable hairball of thousands of edges.</p>
                  <p>But a node&apos;s <b>metrics</b> in the tooltip (degree, betweenness, clustering, strongest connections, the weekly chart) are computed on the <b>&ldquo;Expanded Graph&rdquo;</b> &mdash; <i>every</i> statistically-significant connection (p&nbsp;&lt;&nbsp;0.005), often thousands of edges.</p>
                  <p>So a node can show a high <b>In/Out Degree</b> even when only a few of its links are visible on screen: the number reflects its role in the <i>whole</i> network, not just what&apos;s drawn.</p>
                  <p className="info-tip">In short &mdash; the picture shows the highlights; the numbers tell the full story.</p>
                </>
              )}
              {tab === 'score' && (
                <>
                  <p>The <b>Volatility Watch</b> lists assets that are <b>calm right now</b> but that the model flags as most likely to see <b>elevated volatility over the next ~5 days</b>.</p>
                  <p>Why &ldquo;calm&rdquo;? It&apos;s easy to say a wild stock stays wild &mdash; the useful signal is spotting the <i>quiet</i> ones <i>before</i> they move.</p>
                  <p>What feeds the score:</p>
                  <ul>
                    <li><b>Recent volatility</b> of the asset (the baseline).</li>
                    <li><b>Network position</b> &mdash; notably, peripheral / low-degree assets tend to become more volatile.</li>
                    <li><b>Neighbour activity</b> &mdash; big moves in the assets that strongly influence it (&ldquo;event exposure&rdquo;).</li>
                  </ul>
                  <p>The score is a <b>0&ndash;100 risk estimate</b> (higher = more likely to move). It estimates the <i>size</i> of an upcoming move, not its direction. In out-of-sample testing, flagged calm names became volatile several times more often than the base rate.</p>
                  <p className="info-disclaimer">For research and visualization only &mdash; not investment advice.</p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InfoOverlay;
